import { useState } from "react";
import { InfoCircleFill, KeyFill, QrCode, Pencil } from "react-bootstrap-icons";
import { IntegrationStatus } from "./Data/ApiTransport";
import { ServerRequestsParkerIntegrationPartyDto } from "./shared/api/axios-client";
import { Button } from "./Controls/Button";
import { EditRfidModal } from "./Controls/EditRfidModal";
import { supportsCredentialUpdate } from "./Data/IntegrationCapabilities";
import { getIntegrationDisplayName } from "./Data/IntegrationDisplay";

interface Props {
  integrations: ServerRequestsParkerIntegrationPartyDto[];
  subscriptionId: number;
  facilityId: number;
  setPendingToggle: (data: {
    integrationPartyId: number;
    subscriptionId: number;
    parkerId: number;
    facilityId: number;
    newStatus: IntegrationStatus;
  }) => void;
  setShowToggleConfirm: (show: boolean) => void;
  setQrCodeData: (data: string) => void;
  setShowQrModal: (show: boolean) => void;
  setParkerIntegrations?: React.Dispatch<
    React.SetStateAction<ServerRequestsParkerIntegrationPartyDto[]>
  >;
  hasSubParkers?: boolean;
  hasEnrolledIntegrations?: boolean;
}

export function ParkerIntegrations({
  integrations,
  subscriptionId,
  facilityId,
  setPendingToggle,
  setShowToggleConfirm,
  setQrCodeData,
  setShowQrModal,
  setParkerIntegrations,
  hasSubParkers = false,
  hasEnrolledIntegrations = false,
}: Props) {
  const showManagedViaParkersMessage =
    hasSubParkers && !hasEnrolledIntegrations && integrations.length > 0;
  const [editRfidIntegration, setEditRfidIntegration] =
    useState<ServerRequestsParkerIntegrationPartyDto | null>(null);

  const canEdit = (integration: ServerRequestsParkerIntegrationPartyDto) =>
    supportsCredentialUpdate(integration) &&
    integration.isActive &&
    setParkerIntegrations;

  const handleRfidUpdated = (updated: ServerRequestsParkerIntegrationPartyDto) => {
    if (setParkerIntegrations) {
      setParkerIntegrations((prev) => {
        const keyMatch = (i: ServerRequestsParkerIntegrationPartyDto) =>
          i.integrationPartyId === updated.integrationPartyId &&
          i.parkerId === updated.parkerId &&
          (i.vehicleId ?? undefined) === (updated.vehicleId ?? undefined);
        return prev.some(keyMatch)
          ? prev.map((i) => (keyMatch(i) ? { ...i, ...updated } : i))
          : [...prev, updated];
      });
    }
  };

  return (
    <div className="border rounded mb-4 overflow-hidden">
      <div
        className="d-flex align-items-center gap-2 px-3 py-2 border-bottom"
        style={{ backgroundColor: "color-mix(in srgb, var(--color-light) 35%, white)" }}
      >
        <div
          className="rounded d-flex align-items-center justify-content-center flex-shrink-0 text-white"
          style={{ width: 32, height: 32, backgroundColor: "var(--color-mid)" }}
        >
          <KeyFill />
        </div>
        <strong style={{ color: "var(--color-very-dark)" }}>Access Passes</strong>
      </div>

      <div className="p-3 d-flex flex-column gap-3">
        {showManagedViaParkersMessage ? (
          <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-0">
            <InfoCircleFill color="warning" className="mt-1 flex-shrink-0" />
            <div className="small">
              Access passes are managed via individual parkers below. 
            </div>
          </div>
        ) : integrations.length > 0 ? (
          integrations.map((integration) => (
            <div
              key={`${integration.integrationPartyId}-${integration.parkerId}`}
              className="border rounded p-3 bg-white"
            >
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="fw-bold mb-1">
                    {getIntegrationDisplayName(integration.integrationName)}
                  </div>
                  <div className="text-muted small">
                    
                    <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2">
                      {integration.metaFlashRFIDNumber ? (
                        <span>RFID: {integration.metaFlashRFIDNumber}</span>
                      ) : (
                        <span className="text-muted">RFID: —</span>
                      )}
                      {canEdit(integration) && (
                        <Button
                          className="btn btn-sm py-0 px-2"
                          onClick={() => setEditRfidIntegration(integration)}
                        >
                          <Pencil size={12} className="me-1 icon-shift-up" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-column align-items-end gap-2">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id={`switch-${integration.integrationPartyId}-${integration.parkerId}`}
                      checked={integration.isActive}
                      onChange={() => {
                        const newStatus = integration.isActive
                          ? IntegrationStatus.Deactivated
                          : IntegrationStatus.Activated;

                        setPendingToggle({
                          integrationPartyId: integration.integrationPartyId!,
                          subscriptionId,
                          parkerId: integration.parkerId!,
                          facilityId,
                          newStatus,
                        });
                        setShowToggleConfirm(true);
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`switch-${integration.integrationPartyId}-${integration.parkerId}`}
                    >
                       {" "}
                      {integration.isActive ? "Active" : "Inactive"}
                    </label>
                  </div>

                  {(integration.isActive || integration.parkerId === 0) &&
                    integration.metaFlashRFIDNumber && (
                      <Button
                        className="btn-main py-1 px-3"
                        onClick={() => {
                          setQrCodeData(integration.metaFlashRFIDNumber!);
                          setShowQrModal(true);
                        }}
                      >
                        <QrCode
                          className="me-1"
                          style={{ marginTop: "-2px" }}
                        />
                        View QR Code
                      </Button>
                    )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-muted small mb-2">No active integrations.</div>
        )}
      </div>

      {editRfidIntegration && (
        <EditRfidModal
          show={!!editRfidIntegration}
          setShow={(show) => !show && setEditRfidIntegration(null)}
          integration={editRfidIntegration}
          subscriptionId={subscriptionId}
          parkerId={editRfidIntegration.parkerId!}
          facilityId={facilityId}
          onSuccess={(updated) => {
            handleRfidUpdated(updated);
            setEditRfidIntegration(null);
          }}
        />
      )}
    </div>
  );
}